/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  FiBriefcase,
  FiChevronDown,
  FiClipboard,
  FiClock,
  FiDatabase,
  FiDollarSign,
  FiGrid,
  FiKey,
  FiLink2,
  FiLock,
  FiLogOut,
  FiMapPin,
  FiMessageSquare,
  FiRefreshCw,
  FiShield,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { readStoredUser } from "../../utils/sessionUser";

function AdminPanel() {
  const location = useLocation();
  const [openSection, setOpenSection] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const navScrollRef = useRef(null);
  const sectionRefs = useRef(new Map());
  const currentUser = useMemo(() => readStoredUser(), []);
  const isAdminRole = Number(currentUser?.id) === 1;
  const canGenerateTestReservations = isAdminRole || currentUser?.permissions?.["reservations.generate_test"] === true;

  const isSidebarExpanded = isSidebarPinned || isSidebarOpen;

  const handleLogout = () => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  };

  const handleSidebarEnter = () => {
    if (!isSidebarExpanded) {
      setOpenSection(null);
    }
    setIsSidebarOpen(true);
  };

  const handleSidebarLeave = () => {
    if (isSidebarPinned) return;
    setIsSidebarOpen(false);
    setOpenSection(null);
  };

  const handlePinToggle = () => {
    setIsSidebarPinned((prev) => {
      const next = !prev;
      if (next) {
        if (!isSidebarExpanded) {
          setOpenSection(null);
        }
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
        setOpenSection(null);
      }
      return next;
    });
  };

  const setSectionRef = useCallback((key, node) => {
    if (node) {
      sectionRefs.current.set(key, node);
      return;
    }
    sectionRefs.current.delete(key);
  }, []);

  const uiCss = `
    .adminShell {
      min-height: 100vh;
      width: 100%;
      display: flex;
      gap: 16px;
      padding: 14px;
      background:
        radial-gradient(1100px 520px at 8% -10%, rgba(48, 216, 255, 0.10), transparent 62%),
        radial-gradient(900px 520px at 90% 0%, rgba(168, 85, 247, 0.12), transparent 58%),
        radial-gradient(1100px 680px at 40% 110%, rgba(255, 77, 79, 0.07), transparent 62%);
    }

    @media (max-width: 768px) {
      .adminShell {
        padding: 10px;
        gap: 10px;
      }
    }

    .adminSidebar {
      position: sticky;
      top: 14px;
      height: calc(100vh - 28px);
      width: 78px;
      flex: 0 0 auto;
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.40)) border-box;
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
      transition: width 160ms ease;
    }

    .adminSidebar::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(520px 220px at 20% 0%, rgba(48, 216, 255, 0.12), transparent 60%),
        radial-gradient(420px 220px at 90% 10%, rgba(168, 85, 247, 0.14), transparent 62%);
      pointer-events: none;
      opacity: 0.95;
    }

    .adminSidebar > * {
      position: relative;
      z-index: 1;
    }

    .adminSidebar--open {
      width: 252px;
    }

    .adminSidebar:not(.adminSidebar--open) .adminNavItemText {
      display: none;
    }

    .adminSidebar:not(.adminSidebar--open) .adminChevron {
      display: none;
    }

    .adminSidebar:not(.adminSidebar--open) .adminNav {
      overflow: hidden;
      padding-right: 0;
    }

    .adminSidebarInner {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 12px 8px;
      gap: 10px;
      min-height: 0;
      overflow: hidden;
    }

    .adminSidebarFooter {
      flex: 0 0 auto;
      padding-top: 10px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .adminBrand {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.03);
      user-select: none;
    }

    .adminBrandMark {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      padding: 6px;
      border: 1px solid rgba(48, 216, 255, 0.35);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.18), rgba(168, 85, 247, 0.20));
      color: rgba(230, 230, 247, 0.92);
      font-weight: 900;
      letter-spacing: 0.4px;
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .adminBrandLogo {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38));
    }

    .adminBrandText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      overflow: hidden;
      opacity: 0;
      max-width: 0;
      transition: opacity 160ms ease, max-width 160ms ease;
    }

    .adminSidebar--open .adminBrandText {
      opacity: 1;
      max-width: 160px;
    }

    .adminBrandTitle {
      font-weight: 900;
      color: transparent;
      background: linear-gradient(
        90deg,
        #e6e6f7 0%,
        rgba(48, 216, 255, 0.95) 45%,
        rgba(168, 85, 247, 0.95) 95%
      );
      -webkit-background-clip: text;
      background-clip: text;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .adminBrandSubtitle {
      font-size: 0.72rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .adminNav {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      gap: 6px;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-top: 2px;
      padding-right: 4px;
      padding-bottom: 8px;
      scrollbar-width: thin;
      scrollbar-color: rgba(230, 230, 247, 0.16) transparent;
      scroll-behavior: smooth;
      overscroll-behavior: contain;
    }

    .adminNavSection {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .adminNav > * {
      flex-shrink: 0;
    }

    .adminNav::-webkit-scrollbar {
      width: 4px;
    }

    .adminNav::-webkit-scrollbar-track {
      background: transparent;
    }

    .adminNav::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.16);
      border-radius: 999px;
    }

    .adminNav:hover::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.24);
    }

    .adminNavItem {
      width: 100%;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.02);
      color: rgba(230, 230, 247, 0.88);
      text-decoration: none;
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      padding: 10px 8px;
      text-align: left;
    }

    .adminNavItem:hover {
      background: rgba(48, 216, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.18);
      transform: translateY(-1px);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.25);
    }

    .adminNavItem--active {
      background: rgba(168, 85, 247, 0.12);
      border-color: rgba(168, 85, 247, 0.25);
      box-shadow: 0 18px 50px rgba(168, 85, 247, 0.10);
    }

    .adminNavItemIcon {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.10);
      flex: 0 0 auto;
      color: rgba(230, 230, 247, 0.92);
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    }

    .adminNavItem--active .adminNavItemIcon {
      border-color: rgba(48, 216, 255, 0.28);
      background: radial-gradient(circle at 20% 20%, rgba(48, 216, 255, 0.14), rgba(168, 85, 247, 0.16));
      box-shadow: 0 18px 40px rgba(48, 216, 255, 0.08);
    }

    .adminNavItemText {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      flex: 1 1 auto;
    }

    .adminNavItemLabel,
    .adminNavItemMeta,
    .adminChevron {
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      transition: opacity 160ms ease, max-width 160ms ease, transform 160ms ease;
    }

    .adminNavItemLabel {
      font-weight: 850;
      font-size: 0.95rem;
      color: rgba(230, 230, 247, 0.92);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .adminNavItemMeta {
      font-size: 0.74rem;
      color: rgba(230, 230, 247, 0.55);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .adminChevron {
      margin-left: auto;
      color: rgba(230, 230, 247, 0.55);
      display: grid;
      place-items: center;
    }

    .adminNavItem[aria-expanded="true"] .adminChevron {
      transform: rotate(180deg);
    }

    .adminSidebar--open .adminNavItemLabel,
    .adminSidebar--open .adminNavItemMeta,
    .adminSidebar--open .adminChevron {
      opacity: 1;
      max-width: 220px;
    }

    .adminSidebar--open .adminChevron {
      max-width: 28px;
    }

    .adminSubNav {
      margin-left: 8px;
      margin-right: 6px;
      border-left: 1px solid rgba(255, 255, 255, 0.08);
      padding-left: 12px;
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: opacity 180ms ease;
    }

    .adminSubNav--open {
      max-height: none;
      opacity: 1;
      padding-top: 6px;
      padding-bottom: 8px;
    }

    .adminSidebar:not(.adminSidebar--open) .adminSubNav {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
      pointer-events: none;
    }

    .adminSubItem {
      display: flex;
      align-items: center;
      gap: 10px;
      border-radius: 12px;
      padding: 9px 10px;
      margin: 4px 0;
      color: rgba(230, 230, 247, 0.82);
      text-decoration: none;
      border: 1px solid rgba(255, 255, 255, 0.06);
      background: rgba(255, 255, 255, 0.02);
      transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
    }

    .adminSubItem:hover {
      background: rgba(48, 216, 255, 0.05);
      border-color: rgba(48, 216, 255, 0.16);
      transform: translateY(-1px);
    }

    .adminSubItem--active {
      background: rgba(48, 216, 255, 0.08);
      border-color: rgba(48, 216, 255, 0.22);
      color: rgba(230, 230, 247, 0.92);
    }

    .adminPinButton {
      margin-left: auto;
      width: 28px;
      height: 28px;
      border-radius: 10px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.75);
      display: grid;
      place-items: center;
      cursor: pointer;
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }

    .adminPinButton:hover {
      background: rgba(48, 216, 255, 0.1);
      border-color: rgba(48, 216, 255, 0.28);
      color: rgba(230, 230, 247, 0.95);
    }

    .adminPinButton--active {
      background: rgba(168, 85, 247, 0.18);
      border-color: rgba(168, 85, 247, 0.4);
      color: rgba(230, 230, 247, 0.98);
    }

    .adminMain {
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
      padding: 0 4px;
      display: flex;
      flex-direction: column;
    }

    .adminMainViewport {
      width: 100%;
      height: calc(100vh - 28px);
      min-height: 0;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
    }

    .adminMainViewport > * {
      flex: 1 1 auto;
      min-height: 100%;
      width: 100%;
      box-sizing: border-box;
    }

    .adminCard {
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.35), rgba(168, 85, 247, 0.45), rgba(255, 77, 79, 0.26)) border-box;
      box-shadow: 0 26px 70px rgba(0, 0, 0, 0.52);
      overflow: hidden;
      min-height: 100%;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
    }

    .adminCardInner {
      padding: 18px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .adminCardHeader {
      padding: 18px 18px 14px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .adminCardTitle {
      margin: 0;
      font-weight: 900;
      font-size: 1.1rem;
      color: rgba(230, 230, 247, 0.92);
    }

    .adminCardSubtitle {
      margin-top: 4px;
      font-size: 0.86rem;
      color: rgba(230, 230, 247, 0.62);
    }

    @media (max-height: 820px) {
      .adminSidebar { zoom: 0.9; height: calc((100vh - 28px) / 0.9); }
      .adminMain { zoom: 0.9; }
      .adminMainViewport { height: calc((100vh - 28px) / 0.9); }
    }
    @media (max-height: 720px) {
      .adminSidebar { zoom: 0.8; height: calc((100vh - 28px) / 0.8); }
      .adminMain { zoom: 0.8; }
      .adminMainViewport { height: calc((100vh - 28px) / 0.8); }
    }
    @media (max-height: 620px) {
      .adminSidebar { zoom: 0.7; height: calc((100vh - 28px) / 0.7); }
      .adminMain { zoom: 0.7; }
      .adminMainViewport { height: calc((100vh - 28px) / 0.7); }
    }
  `;

  const menuItems = useMemo(
    () => [
      {
        key: "business-units",
        label: "Business Units",
        meta: "Units & income",
        to: "/admin/business-units",
        Icon: FiBriefcase,
      },
      {
        key: "currencies",
        label: "Currencies",
        meta: "ISO catalog",
        to: "/admin/currencies",
        Icon: FiDollarSign,
      },
      {
        key: "room-categories",
        label: "Room Categories",
        meta: "Types & limits",
        to: "/admin/room-categories",
        Icon: FiGrid,
      },
      {
        key: "rooms-list",
        label: "Rooms",
        meta: "Inventory",
        to: "/admin/rooms-list",
        Icon: FiKey,
      },
      {
        key: "view-db",
        label: "View DB",
        meta: "Raw tables",
        to: "/admin/view-db",
        Icon: FiDatabase,
      },
      {
        key: "measurement-units",
        label: "Units of measure",
        meta: "Pilcore catalogue",
        to: "/admin/measurement-units",
        Icon: FiGrid,
        hidden: !isAdminRole,
      },
      {
        key: "inventory-location-incidents",
        label: "Incidencias de stock",
        meta: "Ubicaciones y avisos",
        to: "/admin/inventory-location-incidents",
        Icon: FiClipboard,
        hidden: !isAdminRole,
      },
      {
        key: "inventory-location-reconciliation",
        label: "Inventory Reconciliation",
        meta: "Location provenance",
        to: "/admin/inventory-location-reconciliation",
        Icon: FiRefreshCw,
        hidden: !isAdminRole,
      },
      {
        key: "cron",
        label: "Cron",
        meta: "Jobs",
        to: "/admin/cron",
        Icon: FiClock,
      },
      {
        key: "reservation-generator",
        label: "Test Reservations",
        meta: "Generate & schedule",
        to: "/admin/reservation-generator",
        Icon: FiClipboard,
        hidden: !canGenerateTestReservations,
      },
      {
        key: "review-intelligence",
        label: "Review Intelligence",
        meta: "Sources & topics",
        activePrefix: "/admin/reviews-intelligence",
        Icon: FiMessageSquare,
        children: [
          {
            key: "review-sources",
            label: "Review Sources",
            to: "/admin/reviews-intelligence/review-sources",
          },
          {
            key: "review-topics-catalog",
            label: "Review Topics Catalog",
            to: "/admin/reviews-intelligence/review-topics-catalog",
          },
        ],
      },
      {
        key: "integrations",
        label: "Integrations",
        meta: "Channels & payments",
        activePrefix: "/admin/integrations",
        Icon: FiLink2,
        children: [
          {
            key: "channel-managers",
            label: "Channel Managers",
            to: "/admin/integrations/channel-managers",
          },
          {
            key: "payment-gateways",
            label: "Payment Gateways",
            to: "/admin/integrations/payment-gateways",
          },
          {
            key: "crm-providers",
            label: "CRM Providers",
            to: "/admin/integrations/crm-providers",
          },
          ...(isAdminRole ? [{
            key: "pos-purchasing",
            label: "POS y Compras",
            to: "/admin/integrations/pos-purchasing",
          }, {
            key: "business-unit-providers",
            label: "Servicios por BU",
            to: "/admin/integrations/business-unit-providers",
          }] : []),
          {
            key: "whatsapp-crm",
            label: "WhatsApp CRM",
            to: "/admin/integrations/whatsapp-crm",
          },
          {
            key: "whatsapp-delivery-notes",
            label: "WhatsApp Delivery Notes",
            to: "/admin/integrations/whatsapp-delivery-notes",
          },
        ],
      },
      {
        key: "market-intelligence",
        label: "Market Intelligence",
        meta: "Competitors",
        to: "/admin/market-intelligence",
        Icon: FiTrendingUp,
      },
      {
        key: "user-management",
        label: "User Management",
        meta: "Roles & access",
        to: "/admin/user-management",
        Icon: FiUsers,
      },
      {
        key: "security",
        label: "Security",
        meta: "Events & access",
        activePrefix: "/admin/security",
        Icon: FiLock,
        children: [
          {
            key: "app-logs",
            label: "Logs",
            to: "/admin/security/app-logs",
          },
          {
            key: "security-logs",
            label: "Security Logs",
            to: "/admin/security/logs",
          },
        ],
      },
      {
        label: "Onboarding",
        key: "onboarding",
        meta: "Setup, drafts & apply",
        to: "/admin/onboarding",
        Icon: FiClipboard,
        hidden: !isAdminRole,
      },
      {
        key: "occupancy-audit",
        label: "Audit",
        meta: "Occupancy checks",
        to: "/admin/occupancy-audit",
        Icon: FiShield,
      },
    ],
    [isAdminRole, canGenerateTestReservations]
  );

  const activeSectionKey = useMemo(() => {
    const activeItem = menuItems
      .filter((item) => !item.hidden && item.children?.length)
      .find((item) => {
        if (
          item.activePrefix &&
          (location.pathname === item.activePrefix ||
            location.pathname.startsWith(`${item.activePrefix}/`))
        ) {
          return true;
        }
        return item.children.some(
          (child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`)
        );
      });

    return activeItem?.key || null;
  }, [location.pathname, menuItems]);

  useEffect(() => {
    const targetSection = openSection || activeSectionKey;

    if (!isSidebarExpanded || !targetSection) {
      return undefined;
    }

    const navElement = navScrollRef.current;
    const sectionElement = sectionRefs.current.get(targetSection);

    if (!navElement || !sectionElement) {
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    const ensureSectionVisible = () => {
      if (!navScrollRef.current || !sectionRefs.current.has(targetSection)) {
        return;
      }

      const currentNav = navScrollRef.current;
      const currentSection = sectionRefs.current.get(targetSection);

      if (!currentNav || !currentSection) {
        return;
      }

      const navRect = currentNav.getBoundingClientRect();
      const sectionRect = currentSection.getBoundingClientRect();
      const safePadding = 12;
      const overflowBottom = sectionRect.bottom - navRect.bottom;
      const overflowTop = navRect.top - sectionRect.top;

      if (overflowBottom > -safePadding) {
        currentNav.scrollTo({
          top: currentNav.scrollTop + overflowBottom + safePadding,
          behavior: "smooth",
        });
        return;
      }

      if (overflowTop > safePadding) {
        currentNav.scrollTo({
          top: Math.max(0, currentNav.scrollTop - overflowTop - safePadding),
          behavior: "smooth",
        });
      }
    };

    frameId = window.requestAnimationFrame(() => {
      ensureSectionVisible();
      timeoutId = window.setTimeout(ensureSectionVisible, 170);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [activeSectionKey, isSidebarExpanded, openSection]);

  return (
    <div className="adminShell">
      <style>{uiCss}</style>

      <aside
        className={`adminSidebar ${isSidebarExpanded ? "adminSidebar--open" : ""} ${
          isSidebarPinned ? "adminSidebar--pinned" : ""
        }`}
        aria-label="Admin navigation"
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      >
        <div className="adminSidebarInner">
          <div className="adminBrand" title="Admin Panel">
            <div className="adminBrandMark">
              <img
                className="adminBrandLogo"
                src={`${process.env.PUBLIC_URL}/Logo_esfera-removebg-preview.png`}
                alt="Pilcore"
              />
            </div>
            <div className="adminBrandText">
              <div className="adminBrandTitle">Admin</div>
              <div className="adminBrandSubtitle">Pilcore</div>
            </div>
            {isSidebarExpanded ? (
              <button
                type="button"
                className={`adminPinButton ${isSidebarPinned ? "adminPinButton--active" : ""}`}
                onClick={handlePinToggle}
                title={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
                aria-pressed={isSidebarPinned}
                aria-label={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          <nav ref={navScrollRef} className="adminNav">
            {menuItems.filter((item) => !item.hidden).map((item) => {
              if (item.children?.length) {
                const isActive = activeSectionKey === item.key;
                const isOpen = openSection === item.key || isActive;
                const Icon = item.Icon;

                return (
                  <div
                    key={item.key}
                    ref={(node) => setSectionRef(item.key, node)}
                    className="adminNavSection"
                  >
                    <button
                      type="button"
                      className={`adminNavItem ${isActive ? "adminNavItem--active" : ""}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection((prev) => (prev === item.key ? null : item.key))}
                      title={item.label}
                    >
                      <span className="adminNavItemIcon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="adminNavItemText">
                        <span className="adminNavItemLabel">{item.label}</span>
                        <span className="adminNavItemMeta">{item.meta}</span>
                      </span>
                      <span className="adminChevron" aria-hidden="true">
                        <FiChevronDown size={16} />
                      </span>
                    </button>

                    <div className={`adminSubNav ${isOpen ? "adminSubNav--open" : ""}`}>
                      {item.children.map((child) => (
                        <NavLink
                          key={child.key}
                          to={child.to}
                          title={child.label}
                          className={({ isActive: subActive }) =>
                            `adminSubItem ${subActive ? "adminSubItem--active" : ""}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              const Icon = item.Icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  title={item.label}
                  className={({ isActive }) => `adminNavItem ${isActive ? "adminNavItem--active" : ""}`}
                >
                  <span className="adminNavItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="adminNavItemText">
                    <span className="adminNavItemLabel">{item.label}</span>
                    <span className="adminNavItemMeta">{item.meta}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
          <div className="adminSidebarFooter">
            <button
              type="button"
              className="adminNavItem"
              onClick={handleLogout}
              title="Log out"
              aria-label="Log out"
            >
              <span className="adminNavItemIcon" aria-hidden="true">
                <FiLogOut size={18} />
              </span>
              <span className="adminNavItemText">
                <span className="adminNavItemLabel">Log out</span>
              </span>
            </button>
          </div>
        </div>
      </aside>

      <main className="adminMain">
        <div className="adminMainViewport">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default AdminPanel;
